'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  createWaypoint,
  errorIndices,
  validateRoute,
  type KeepInFence,
  type PlanWaypoint,
  type RouteViolation,
} from '@/lib/mission-planning';
import {
  MAX_ROUTE_WAYPOINTS,
  effectiveWaypointAltitude,
  makeDraftWaypoint,
  type DraftWaypoint,
} from '../missions/_components/missionCanvasShared';

// ---------------------------------------------------------------------------
// A route being drawn on the live COP map — a new mission being composed, or an
// existing one being revised. Both are the same act on the same overlay; what
// differs is where the points came from and where they go, which is the
// caller's business.
//
// Deliberately thin: the geometry, the gates and the commit all already exist
// (`lib/mission-planning.ts`, `missionCanvasShared.ts`, `useMissionCommit.ts`).
// What did not exist was somewhere to hold points the operator is clicking onto
// the COP, and the adapter between the two shapes that meet here — the mission
// vocabulary's `DraftWaypoint {id, lat, lng}` and the planner's `PlanWaypoint`,
// which carries the altitude the keep-in check needs.
//
// The validation is the part the full-screen canvas never had: it draws routes
// without ever asking whether they stay inside an active keep-in fence, so an
// operator found out at launch. On the COP the fences are on screen, so a
// waypoint outside one is drawn red as it is placed.
// ---------------------------------------------------------------------------

export interface DrawnRouteArgs {
  /**
   * The MISSION altitude — the default a waypoint flies at until the operator gives that point
   * one of its own. Each waypoint is validated at its own effective altitude, not at this one:
   * validating an overridden point against the mission default would make the verdict describe a
   * route nobody is going to fly.
   */
  altitudeM: number;
  /** Active keep-in ('operational') fences. Empty means nothing to contain. */
  keepInFences: KeepInFence[];
  /**
   * How many points this route may hold (022) — the mission type's own ceiling.
   *
   * Defaults to the route cap, which is what every caller had before mission types existed.
   * A ceiling of 1 does not mean "refuse the second click": it means the route holds one
   * point and clicking again MOVES it, which is how an orbit centre is placed and what the
   * map banner promises. Refusing there would leave an operator clicking at a centre that
   * never moves with no way to tell why.
   */
  maxWaypoints?: number;
}

export interface DrawnRouteController {
  waypoints: DraftWaypoint[];
  selectedIndex: number | null;
  violations: RouteViolation[];
  /** Indices with a blocking violation — these render red on the map. */
  invalidIndices: Set<number>;
  /** True once the route is at the cap; further clicks are refused, not silently dropped. */
  atCap: boolean;
  /** Append a point from a map click. Returns false when the cap refused it. */
  addFromMap: (lat: number, lng: number) => boolean;
  /** Live drag reposition from the map. */
  dragTo: (index: number, lat: number, lng: number) => void;
  select: (index: number | null) => void;
  removeAt: (index: number) => void;
  /**
   * Give one point its own altitude, or `null` to put it back on the mission altitude.
   *
   * Selection is untouched: changing an altitude is not a change of attention, and moving the
   * map's highlight because the operator typed in a field they were already looking at would be
   * a second, unasked-for action.
   */
  setAltitudeAt: (index: number, altitudeM: number | null) => void;
  clear: () => void;
  /**
   * Replace the whole route — a saved mission's stored waypoints being loaded
   * for revision. Not `clear` + N adds: that would renumber and re-key every
   * point through the cap check for a route the server already accepted.
   */
  load: (waypoints: DraftWaypoint[]) => void;
}

export function useDrawnRoute({
  altitudeM,
  keepInFences,
  maxWaypoints = MAX_ROUTE_WAYPOINTS,
}: DrawnRouteArgs): DrawnRouteController {
  const [waypoints, setWaypoints] = useState<DraftWaypoint[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  /**
   * The route as it stands, readable synchronously.
   *
   * Map clicks arrive faster than React commits, and the alternative — a
   * `setWaypoints` updater — cannot also move the selection without nesting one
   * setState inside another's updater. Kept in step on every write below, so two
   * clicks in the same frame both land and the second still sees the first.
   */
  const waypointsRef = useRef<DraftWaypoint[]>(waypoints);

  const commit = useCallback((next: DraftWaypoint[], selection: number | null) => {
    waypointsRef.current = next;
    setWaypoints(next);
    setSelectedIndex(selection);
  }, []);

  /**
   * A single-point route is never "at cap" in the sense the banner and the strip mean it —
   * the operator is not being stopped from adding, they are moving the one point they have.
   * Reporting a cap there would put a "route is full" warning on a mission that is complete.
   */
  const atCap = maxWaypoints > 1 && waypoints.length >= maxWaypoints;

  const addFromMap = useCallback((lat: number, lng: number): boolean => {
    const prev = waypointsRef.current;
    // A one-point route MOVES rather than fills up: this is an orbit centre or a GO TO POINT
    // destination, and the second click means "not there, here". A HOVER is deliberately not
    // in that list since #373 — it may carry a route, so its second click APPENDS.
    if (maxWaypoints === 1) {
      // Move the existing point rather than replace it, so a per-point altitude the operator
      // typed survives. Building a fresh waypoint here dropped that override silently — which
      // `dragTo` never does, and which this file's own rule forbids: an altitude the operator
      // set is reported on, never quietly rewritten.
      const existing = prev[0];
      const moved = existing ? { ...existing, lat, lng } : makeDraftWaypoint(lat, lng);
      commit([moved], 0);
      return true;
    }
    // Refused, not silently dropped: an operator clicking a point that never
    // appears has to be told why.
    if (prev.length >= maxWaypoints) return false;
    const next = [...prev, makeDraftWaypoint(lat, lng)];
    commit(next, next.length - 1);
    return true;
  }, [commit, maxWaypoints]);

  const dragTo = useCallback((index: number, lat: number, lng: number) => {
    const next = waypointsRef.current.map((wp, i) => (i === index ? { ...wp, lat, lng } : wp));
    commit(next, index);
  }, [commit]);

  const select = useCallback((index: number | null) => setSelectedIndex(index), []);

  const removeAt = useCallback((index: number) => {
    const next = waypointsRef.current.filter((_, i) => i !== index);
    waypointsRef.current = next;
    setWaypoints(next);
    // Keep the selection pointing at the same POINT, not the same slot: after
    // removing WP2, what was WP3 is now WP2, and selecting it would move the
    // operator's attention somewhere they did not ask for.
    setSelectedIndex((sel) => {
      if (sel === null || sel === index) return null;
      return sel > index ? sel - 1 : sel;
    });
  }, []);

  const setAltitudeAt = useCallback((index: number, altitudeM: number | null) => {
    const next = waypointsRef.current.map((wp, i) => {
      if (i !== index) return wp;
      // Clearing the override drops the key entirely rather than storing the mission altitude:
      // "follows the mission" and "happens to equal it right now" have to stay distinguishable,
      // or raising the mission altitude would leave this point behind for no visible reason.
      if (altitudeM === null) {
        if (wp.altitudeM === undefined) return wp;
        const cleared = { ...wp };
        delete cleared.altitudeM;
        return cleared;
      }
      return { ...wp, altitudeM };
    });
    waypointsRef.current = next;
    setWaypoints(next);
  }, []);

  const clear = useCallback(() => commit([], null), [commit]);

  const load = useCallback(
    (next: DraftWaypoint[]) => commit(next, next.length > 0 ? 0 : null),
    [commit],
  );

  /**
   * The planner's shape — the altitude is what makes a keep-in verdict meaningful.
   *
   * Per point, not per route: a waypoint the operator raised to 300 m has to be checked at 300 m.
   * Validating it at the mission default would report a containment verdict for a route that does
   * not exist, which is worse than no verdict at all.
   */
  const planWaypoints = useMemo<PlanWaypoint[]>(
    () => waypoints.map((wp) => createWaypoint(wp.lat, wp.lng, { alt: effectiveWaypointAltitude(wp, altitudeM) })),
    [waypoints, altitudeM],
  );

  /**
   * Always homeless, and not by omission.
   *
   * `validateRoute`'s `home` is the LAUNCH point, and the only check it feeds is
   * "how far is this waypoint from the dock". A route drawn on the COP has no
   * launch point: compose's site is metadata (a name on the mission record, not
   * a takeoff pad) and a route being revised has none at all. Hard-coded here
   * rather than accepted as an argument, because an argument only one value can
   * ever be is a promise the caller cannot keep. The dock launch planner
   * (`useDockMissionPlan`) does have a real one and passes it.
   */
  const violations = useMemo(
    () => validateRoute(planWaypoints, { home: null, keepInFences }),
    [planWaypoints, keepInFences],
  );

  const invalidIndices = useMemo(() => errorIndices(violations), [violations]);

  return {
    waypoints,
    selectedIndex,
    violations,
    invalidIndices,
    atCap,
    addFromMap,
    dragTo,
    select,
    removeAt,
    setAltitudeAt,
    clear,
    load,
  };
}
